import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getDevices,
  getPosPrintersExist,
  createDevice,
  updateDevice,
  deleteDevice,
  getFiscalApiLatest,
} from "../api/devices";
import type {
  CreateDeviceRequest,
  UpdateDeviceRequest,
} from "../api/devices";

// Хук за извличане на всички устройства — GET /devices
export const useDevices = () => {
  return useQuery({
    queryKey: ["devices"],
    queryFn: getDevices,
  });
};

// Има ли заведението изобщо POS принтер — гейт за въпроса „Печат от POS
// принтер?" при нулиране на отчет. enabled пази заявката само за отворен модал.
export const usePosPrintersExist = (enabled: boolean) => {
  return useQuery({
    queryKey: ["devices", "pos-exists"],
    queryFn: getPosPrintersExist,
    enabled,
  });
};

// Последната публикувана FiscalAPI версия (download линк + база за
// „има по-нова версия" до инсталациите). BE-то кешира манифеста 5 мин —
// същият staleTime тук спестява излишните заявки при навигация.
export const useFiscalApiLatest = () => {
  return useQuery({
    queryKey: ["fiscalapi-latest"],
    queryFn: getFiscalApiLatest,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

// Хук за създаване на устройство — POST /devices
export const useCreateDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDeviceRequest) => createDevice(data),
    onSuccess: () => {
      // Промяна на device.paymentMethods → активният списък в Ordering се сменя.
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
    },
  });
};

// Хук за редактиране на устройство — PUT /devices/:id
export const useUpdateDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateDeviceRequest;
    }) => updateDevice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
    },
  });
};

// Хук за изтриване на устройство — DELETE /devices/:id
export const useDeleteDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
    },
  });
};
